import React, {useCallback, useState} from 'react';
import {Button, EMPHASIS, FlexRow, Icon, Label, Text, TextArea} from '@forecast-it/design-system';
import {useIntl} from 'react-intl';
import {ClientPrimaryContactDropdown} from '../../../forecast-app/shared/components/dropdowns/client-primary-contact-dropdown/ClientPrimaryContactDropdown';
import {MAX_REASON_LENGTH, QuoteDecision, recordQuoteDecision} from './recordQuoteDecision';
import {DecisionPanel, FieldRow} from './QuoteDecisionPanel.styled';

export type QuoteDecisionPanelProps = {
	/** Relay global ID of the quote being decided. */
	quoteId: string;
	/**
	 * Relay global ID of the project's client, or null when the project has no client. Passed
	 * straight into the picker's `company.client(id:)` query, which is why it is the global ID and
	 * not a numeric id.
	 */
	clientId: string | null;
	decision: QuoteDecision;
	/** Closes the panel and returns the card to its action row. */
	onCancel: () => void;
	/** Called after a successful decision, so the caller can refetch or track. */
	onDecided?: () => void;
};

type Contact = {id: string; name: string};

/**
 * Id for the Confirm button's label text, so the button can point `aria-labelledby` at it.
 *
 * BaseButton derives the accessible name from `children.toString()` — the literal "[object Object]"
 * for the JSX child a coloured icon requires — and applies it after spreading the caller's props, so
 * an `aria-label` of ours would be overwritten. `aria-labelledby` survives the spread and outranks
 * `aria-label` when the name is computed. Static because only one panel is ever open at a time.
 */
const CONFIRM_LABEL_ID = 'quote-decision-confirm-label';

/** Per-decision copy and identifiers. The only thing that differs between Approve and Decline. */
const COPY = {
	APPROVED: {
		dataCy: 'approve-quote-panel',
		titleId: 'quote_activity.approve_modal.title',
		confirmId: 'quote_activity.approve_confirm',
		noClientId: 'quote_activity.approve_modal.no_client',
		noContactsId: 'quote_activity.approve_modal.no_contacts',
		icon: 'checkCircle',
		iconColor: 'success',
		showsReason: false,
	},
	DECLINED: {
		dataCy: 'decline-quote-panel',
		titleId: 'quote_activity.decline_modal.title',
		confirmId: 'quote_activity.decline_confirm',
		noClientId: 'quote_activity.decline_modal.no_client',
		noContactsId: 'quote_activity.decline_modal.no_contacts',
		icon: 'closeCircle',
		iconColor: 'error',
		showsReason: true,
	},
} as const;

/**
 * The decision form, rendered inline inside the Quote Activity card rather than as a modal, so
 * recording an outcome keeps the log it relates to on screen.
 *
 * One component for both decisions. They differ only by title, confirm label, whether the reason
 * textarea renders, which blocked-state copy is used, and the decision sent — so the state matrix
 * lives here exactly once instead of drifting across two near-identical files.
 */
export const QuoteDecisionPanel = ({
	quoteId,
	clientId,
	decision,
	onCancel,
	onDecided,
}: QuoteDecisionPanelProps): JSX.Element => {
	const {formatMessage} = useIntl();
	const copy = COPY[decision];
	const [contact, setContact] = useState<Contact | null>(null);
	const [reason, setReason] = useState('');
	const [submitting, setSubmitting] = useState(false);
	// The picker owns the contacts fetch, so it is the only thing that can tell us the client has
	// none — which is what separates "client with no contacts" from "client with contacts".
	const [clientHasNoContacts, setClientHasNoContacts] = useState(false);

	const hasClient = !!clientId;
	// Confirm is NEVER enabled without a contact: the contacts module is on for every customer, so
	// every decision the UI submits carries one. The no-contacts case falls out of this for free.
	const canConfirm = hasClient && !!contact && !submitting;

	const blockedMessageId = !hasClient ? copy.noClientId : clientHasNoContacts ? copy.noContactsId : null;
	// Confirm is disabled until a contact is chosen, so say which of the three reasons it is rather
	// than leaving a dead button with no explanation.
	const confirmHint = blockedMessageId
		? formatMessage({id: blockedMessageId})
		: contact
		? undefined
		: formatMessage({id: 'quote_activity.select_contact_first'});

	const handleConfirm = useCallback(() => {
		if (!contact) return;
		setSubmitting(true);
		recordQuoteDecision(
			{
				quoteId,
				decision,
				clientContactId: contact.id,
				reason: copy.showsReason ? reason : null,
			},
			() => {
				if (onDecided) onDecided();
				onCancel();
			},
			() => {
				// Stay open on failure so the user does not retype the reason. Util.CommitMutation
				// has already surfaced the readable error modal.
				setSubmitting(false);
			}
		);
	}, [contact, quoteId, decision, reason, copy.showsReason, onDecided, onCancel]);

	return (
		<DecisionPanel data-cy={copy.dataCy}>
			<Label>{formatMessage({id: copy.titleId})}</Label>

			{hasClient && (
				<FieldRow>
					<Label>{formatMessage({id: 'quote_activity.contact_label'})}</Label>
					<ClientPrimaryContactDropdown
						clientId={clientId as string}
						value={contact}
						onChange={setContact}
						onEmpty={() => setClientHasNoContacts(true)}
						hideLabel
						dataCy="quote-decision-contact-picker"
					/>
				</FieldRow>
			)}

			{/* A blocking precondition, not a hint: the decision cannot be recorded until it is
			    resolved, so it reads as an error rather than muted guidance. */}
			{blockedMessageId && (
				<Text size="1" color="error">
					{formatMessage({id: blockedMessageId})}
				</Text>
			)}

			{copy.showsReason && (
				<TextArea
					label={formatMessage({id: 'quote_activity.decline_modal.reason_label'})}
					placeholder={formatMessage({id: 'quote_activity.decline_modal.reason_placeholder'})}
					isOptional
					maxLength={MAX_REASON_LENGTH}
					value={reason}
					// TextArea's onChange receives the VALUE, not an event.
					onChange={(value: string) => setReason(value)}
					// Required by TextArea's props even though the value is already tracked on every
					// change, so there is nothing to commit on blur.
					onBlur={() => undefined}
					data-cy="quote-decline-reason"
				/>
			)}

			<FlexRow gap="s" alignItems="center" justifyContent="flex-end">
				<Button onPress={onCancel} emphasis={EMPHASIS.LOW} data-cy="quote-decision-cancel">
					{formatMessage({id: 'common.cancel'})}
				</Button>
				<Button
					onPress={handleConfirm}
					disabled={!canConfirm}
					emphasis={EMPHASIS.HIGH}
					title={confirmHint}
					aria-labelledby={CONFIRM_LABEL_ID}
					data-cy="quote-decision-confirm"
				>
					<FlexRow gap="s" alignItems="center">
						<Icon icon={copy.icon} size="s" color={canConfirm ? copy.iconColor : 'disabled'} />
						<span id={CONFIRM_LABEL_ID}>{formatMessage({id: copy.confirmId})}</span>
					</FlexRow>
				</Button>
			</FlexRow>
		</DecisionPanel>
	);
};
